///
/// EarthBrowser
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { EntityCameraAdapter } from "@/earthbrowser/datasets/EntityCameraAdapter"
import { type InfoPanelData, type InfoPanelProvider } from "@/earthbrowser/datasets/InfoPanelProvider"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { geographic } from "@/earthbrowser/utils/geographic"
import { pushTargetToHistory } from "@/utils/navigationHistory"
import type { Camera } from "@stovokor/camera/Camera"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type FocusTransitionConfig } from "@stovokor/gestures/FocusTarget"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { vec3 } from "@stovokor/math"
import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { type DatasetSceneObject } from "../DatasetLayer"
import { WildfireMarkerNode, type WildfireMarkerStyle } from "./WildfireMarkerNode"
import { WildfirePerimeterNode, type WildfireGeometry } from "./WildfirePerimeterNode"
import { fireAcreage, isFireActive, type WildfireProperties } from "./WildfiresTable"

const cFireModelAltitude = 0.5
const cFireFocusMinDistance = 15
const cFireFocusMaxZoomRadius = 200
const cKmPerDegree = 111.2

// Clearance above the highest sampled terrain so the flat fill plane clears
// ridges inside the perimeter that the ring samples miss
const cPerimeterClearanceKm = 0.4
// Max terrain samples along a perimeter ring when finding its high point
const cMaxRingElevationSamples = 24

// Only draw the perimeter polygons once they'd be meaningfully visible:
// apparent angular radius above ~0.004 rad (~5px at 1080p). Below that the
// marker dome carries the visual, and skipping the fill/outline draw calls
// keeps the frame rate up at global zoom where every fire would otherwise
// submit geometry each frame. Focused fires always show their perimeter.
const cPerimeterMinAngular = 0.004

// Unload built perimeter geometry after it's been out of view for a while,
// so panning across many fires doesn't accumulate geometry forever. The
// hysteresis (unload only at half the build threshold) plus the delay keep
// a camera hovering near the build distance from thrashing rebuilds.
const cPerimeterUnloadAngular = cPerimeterMinAngular * 0.5
const cPerimeterUnloadDelayMs = 15_000

export class WildfireFeature extends GestureNode implements DatasetSceneObject<WildfireProperties>, InfoPanelProvider {
	public readonly focusable: boolean = true
	public readonly id: string

	public feature: WildfireProperties
	public coordinate: geographic

	private readonly cameraAdapter: EntityCameraAdapter
	private readonly featureContext: EarthBrowserContext

	private readonly marker: WildfireMarkerNode
	private perimeter?: WildfirePerimeterNode
	/** Geometry was missing or malformed; don't retry the parse every frame */
	private perimeterFailed = false
	/** When the built perimeter dropped below the unload threshold (ms epoch), 0 = visible */
	private perimeterHiddenAt = 0
	private hitSphere: sphere
	private readonly modelRotation: matrix
	/** Half the perimeter bbox diagonal in km, used for hit test and framing */
	private readonly extentRadiusKm: number
	/** False until terrain elevation was applied with a loaded elevation layer */
	private elevationResolved = false
	private terrainVersion = -1
	private focused = false

	constructor(context: EarthBrowserContext, feature: WildfireProperties) {
		super(context)
		this.id = feature.id
		this.feature = feature
		this.featureContext = context

		this.coordinate = new geographic(
			feature.centroid_lon * trig.rad,
			feature.centroid_lat * trig.rad,
			cFireModelAltitude
		)

		const xRot = matrix.axisRotation(vec3.unitX, this.coordinate.latitude + trig.half_pi)
		const yRot = matrix.axisRotation(vec3.unitY, this.coordinate.longitude + trig.pi)
		this.modelRotation = yRot.premultiply(xRot)

		this.applyTerrainElevation()

		this.extentRadiusKm = this.computeExtentRadiusKm(feature)

		this.marker = new WildfireMarkerNode(context, this.markerStyle())
		this.addNode(this.marker)

		// The perimeter geometry (JSON parse, triangulated fill, outline
		// polylines) is built lazily in render() on the first frame it would
		// actually be visible. Building all fires eagerly at startup holds
		// hundreds of thousands of vertices for perimeters that are never
		// zoomed into, which was enough to kill memory-constrained mobile tabs.

		// Clicking anywhere on or near the burn area selects the fire, so the
		// polygon itself is effectively the click target once zoomed in
		this.hitSphere = new sphere(Math.max(this.marker.radius, this.extentRadiusKm))

		// Frame the whole perimeter when focused: distance scales with the
		// fire's extent so selecting a fire shows its full shape
		const focusDistance = Math.max(cFireFocusMinDistance, this.extentRadiusKm * 2.8)
		const maxZoomRadius = Math.max(cFireFocusMaxZoomRadius, this.extentRadiusKm * 8)
		this.cameraAdapter = new EntityCameraAdapter(context, this, focusDistance, maxZoomRadius)
	}

	/**
	 * Sit the marker on the terrain: ellipsoid + base elevation + offset,
	 * matching the webcam marker pattern. The globe renders real elevation,
	 * so a fixed sea-level altitude buries fire geometry under mountains.
	 * The elevation layer loads async at startup; until it's ready this
	 * places everything at sea level and render() re-applies once loaded.
	 */
	private applyTerrainElevation(): void {
		this.terrainVersion = this.featureContext.terrainVersion
		const elevationKm = this.featureContext.displayElevationKm(this.coordinate)
		this.coordinate = new geographic(
			this.coordinate.longitude,
			this.coordinate.latitude,
			elevationKm + cFireModelAltitude
		)
		this.transform = matrix.translate(this.coordinate.cartesian)
			.premultiply(this.modelRotation)
		this.elevationResolved = this.featureContext.baseElevation.loaded
	}

	/**
	 * Highest terrain (km) sampled at the centroid and along each outer
	 * ring, so the flat perimeter plane drapes above every ridge it crosses
	 */
	private maxTerrainElevationKm(geometry: WildfireGeometry): number {
		const elevation = this.featureContext.baseElevation
		let maxMeters = elevation.getElevation(this.coordinate)
		const exaggeration = this.featureContext.terrainExaggeration

		const polygons: number[][][][] =
			geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates
		for (const rings of polygons) {
			const outer = rings[0]
			if (!outer) continue
			const step = Math.max(1, Math.floor(outer.length / cMaxRingElevationSamples))
			for (let i = 0; i < outer.length; i += step) {
				const [lon, lat] = outer[i]
				const meters = elevation.getElevation(new geographic(lon * trig.rad, lat * trig.rad, 0))
				if (meters > maxMeters) {
					maxMeters = meters
				}
			}
		}
		return maxMeters > 0 ? maxMeters / 1000 * exaggeration : 0
	}

	private computeExtentRadiusKm(feature: WildfireProperties): number {
		const midLat = (feature.bbox_min_lat + feature.bbox_max_lat) / 2
		const dLatKm = (feature.bbox_max_lat - feature.bbox_min_lat) * cKmPerDegree
		const dLonKm = (feature.bbox_max_lon - feature.bbox_min_lon) * cKmPerDegree * Math.cos(midLat * trig.rad)
		return Math.max(Math.sqrt(dLatKm * dLatKm + dLonKm * dLonKm) / 2, 1)
	}

	private markerStyle(): WildfireMarkerStyle {
		const dateCurrentMs = Date.parse(this.feature.date_current)
		return {
			acres: fireAcreage(this.feature),
			active: isFireActive(this.feature),
			dateCurrentMs: Number.isNaN(dateCurrentMs) ? 0 : dateCurrentMs,
		}
	}

	/**
	 * The perimeter node is positioned in world space (its vertices are
	 * absolute ECEF), so it must not inherit this feature's centroid
	 * transform; parent it with an inverse transform child instead.
	 */
	private buildPerimeter(): WildfirePerimeterNode | undefined {
		try {
			let geometry = JSON.parse(this.feature.geometry) as WildfireGeometry | string
			if (typeof geometry === "string") {
				// Tolerate double-encoded geometry from early ingests, where the
				// jsonb column stored a JSON string instead of a JSON object
				geometry = JSON.parse(geometry) as WildfireGeometry
			}
			if (geometry?.type !== "Polygon" && geometry?.type !== "MultiPolygon") {
				this.perimeterFailed = true
				return undefined
			}
			const altitudeKm = this.maxTerrainElevationKm(geometry) + cPerimeterClearanceKm
			const perimeter = new WildfirePerimeterNode(this.featureContext, geometry, isFireActive(this.feature), altitudeKm)
			perimeter.transform = this.transform.invert()
			this.perimeter = perimeter
			this.addNode(perimeter)
			return perimeter
		} catch {
			// Malformed geometry: the fire still exists as a marker
			this.perimeterFailed = true
			return undefined
		}
	}

	private unloadPerimeter(): void {
		if (!this.perimeter) return
		this.removeNode(this.perimeter)
		this.perimeter.destroy()
		this.perimeter = undefined
		this.perimeterHiddenAt = 0
	}

	/** Rebuild an already-built perimeter in place (elevation or data change).
	 *  Unbuilt perimeters stay unbuilt: the lazy build in render() will use
	 *  the fresh feature data and elevation when it eventually runs. */
	private rebuildPerimeter(): void {
		if (!this.perimeter) return
		this.unloadPerimeter()
		// Preserve the selected styling across the rebuild
		this.buildPerimeter()?.setFocused(this.focused)
	}

	/**
	 * Per-frame maintenance:
	 * - Features built before the async elevation layer finished loading sit
	 *   at sea level (possibly buried under terrain); re-drape them onto the
	 *   terrain as soon as the elevation data is available
	 * - Distance-gate the perimeter polygons so they only render once the
	 *   camera is close enough for them to be relevant
	 */
	override render(state: RenderState): RenderActionSet[] {
		if ((!this.elevationResolved || this.terrainVersion !== this.featureContext.terrainVersion) && this.featureContext.baseElevation.loaded) {
			this.applyTerrainElevation()
			this.rebuildPerimeter()
		}

		const distance = state.camera.position.subtract(this.transform.position).length
		const wantPerimeter = this.focused
			|| this.extentRadiusKm > distance * cPerimeterMinAngular

		// Lazy build on the first frame the perimeter would be visible
		if (wantPerimeter && !this.perimeter && !this.perimeterFailed) {
			this.buildPerimeter()?.setFocused(this.focused)
		}

		if (this.perimeter) {
			this.perimeter.visible = wantPerimeter

			// Unload geometry that's been well out of view for a while
			if (wantPerimeter) {
				this.perimeterHiddenAt = 0
			} else if (this.extentRadiusKm < distance * cPerimeterUnloadAngular) {
				const now = performance.now()
				if (this.perimeterHiddenAt === 0) {
					this.perimeterHiddenAt = now
				} else if (now - this.perimeterHiddenAt > cPerimeterUnloadDelayMs) {
					this.unloadPerimeter()
				}
			}
		}

		return super.render(state)
	}

	/** Called on every dataset poll; rebuilds the perimeter when it changed */
	public updateFeature(feature: WildfireProperties): void {
		const perimeterChanged = feature.date_current !== this.feature.date_current
			|| feature.geometry.length !== this.feature.geometry.length
		this.feature = feature

		this.marker.updateStyle(this.markerStyle())
		this.hitSphere = new sphere(Math.max(this.marker.radius, this.extentRadiusKm))

		if (perimeterChanged) {
			// New geometry may parse fine even if the old one failed
			this.perimeterFailed = false
			this.rebuildPerimeter()
		} else {
			this.perimeter?.updateStyle(isFireActive(feature))
		}
	}

	// ========== InfoPanelProvider ==========

	public getInfoPanelData(): InfoPanelData {
		return {
			title: this.feature.name,
			subtitle: this.feature.county && this.feature.state
				? `${this.feature.county} County, ${this.feature.state}`
				: this.feature.state ?? undefined,
			theme: "wildfire",
			contentType: "wildfire",
			contentData: {
				type: "wildfire",
				wildfire: this.feature
			}
		}
	}

	// ========== Focus Lifecycle ==========

	public onFocusGained(camera: Camera): void {
		this.focused = true
		this.cameraAdapter.attach(camera)
		this.marker.setFocused(true)
		this.perimeter?.setFocused(true)

		if (this.feature.id) {
			pushTargetToHistory('wildfire', String(this.feature.id))
		}
	}

	public onFocusLost(): void {
		this.focused = false
		this.cameraAdapter.detach()
		this.marker.setFocused(false)
		this.perimeter?.setFocused(false)
	}

	public onFocusUpdate(): void {
		this.cameraAdapter.update()
	}

	public focusTransition(camera: Camera): FocusTransitionConfig {
		return {
			target: () => this.cameraAdapter.getTargetCameraState(camera),
			params: {
				duration: 1,
				easing: "easeInOut"
			}
		}
	}

	// ========== Command Handlers ==========

	public handleCommand(command: string, data?: unknown): boolean {
		return this.cameraAdapter.handleCommand(command, data)
	}

	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		// Inflate the hit sphere the same way the marker inflates on screen,
		// so far-away fires rendered at their minimum apparent size stay clickable
		const cameraDistance = state.camera.position.subtract(this.transform.position).length
		const effectiveRadius = Math.max(
			this.marker.radius,
			this.extentRadiusKm,
			cameraDistance * this.marker.minAngularSize
		)
		if (effectiveRadius !== this.hitSphere.radius) {
			this.hitSphere = new sphere(effectiveRadius)
		}

		const dist = this.hitSphere.intersect(state.ray)
		if (dist > 0) {
			const hitPoint = state.ray.pointAt(dist)
			return { node: this, hitPoint: hitPoint, distance: dist }
		}
		return undefined
	}
}
